import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { toast } from 'react-toastify';
import api from '../../api';

const STATUS_OPTIONS = ['PRESENT', 'ABSENT', 'HALF_DAY', 'LEAVE', 'HOLIDAY'];

const STATUS_COLOR = {
  PRESENT: 'success',
  ABSENT: 'error',
  HALF_DAY: 'warning',
  LEAVE: 'info',
  HOLIDAY: 'default',
};

const INITIAL_PAGE_INFO = {
  total: 0,
  total_pages: 1,
  per_page: 20,
  current_page: 1,
};

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMonthInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const toOffsetISOString = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const date = new Date(`${dateStr}T${timeStr}:00`);
  if (Number.isNaN(date.getTime())) return null;

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHour = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const offsetMin = String(absOffset % 60).padStart(2, '0');

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}:${second}${sign}${offsetHour}:${offsetMin}`;
};

const formatDateTime = (value) => {
  if (!value) return 'NA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleString();
};

export default function AttendanceManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeError, setEmployeeError] = useState('');

  const [markDate, setMarkDate] = useState(formatDateInput(new Date()));
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  const [listFilters, setListFilters] = useState({
    from: formatDateInput(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
    to: formatDateInput(new Date()),
    employeeId: '',
    status: '',
  });
  const [listRows, setListRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [listPageInfo, setListPageInfo] = useState(INITIAL_PAGE_INFO);
  const [rowSavingId, setRowSavingId] = useState(null);
  const [rowDraft, setRowDraft] = useState({});

  const [summaryFilters, setSummaryFilters] = useState({
    month: formatMonthInput(new Date()),
    employeeId: '',
  });
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const [calendarFilters, setCalendarFilters] = useState({
    month: formatMonthInput(new Date()),
    employeeId: '',
  });
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState('');

  const totalPayableDays = useMemo(
    () => summaryRows.reduce((sum, row) => sum + Number(row?.payableDays || 0), 0),
    [summaryRows]
  );

  const summaryChartSeries = useMemo(
    () => [
      { name: 'Present', data: summaryRows.map((r) => Number(r.present || 0)) },
      { name: 'Absent', data: summaryRows.map((r) => Number(r.absent || 0)) },
      { name: 'Half Day', data: summaryRows.map((r) => Number(r.halfDay || 0)) },
      { name: 'Leave', data: summaryRows.map((r) => Number(r.leave || 0)) },
      { name: 'Holiday', data: summaryRows.map((r) => Number(r.holiday || 0)) },
    ],
    [summaryRows]
  );

  const summaryChartOptions = useMemo(
    () => ({
      chart: { stacked: true, toolbar: { show: false } },
      xaxis: { categories: summaryRows.map((row) => row.employeeName || `Emp ${row.employeeId}`) },
      legend: { position: 'top' },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
      colors: ['#2e7d32', '#d32f2f', '#ed6c02', '#0288d1', '#9e9e9e'],
    }),
    [summaryRows]
  );

  const calendarGrid = useMemo(() => {
    if (!calendarFilters.month) return [];
    const [year, month] = calendarFilters.month.split('-').map(Number);
    if (!year || !month) return [];

    const firstDay = new Date(year, month - 1, 1);
    const totalDays = new Date(year, month, 0).getDate();
    const startDay = firstDay.getDay();

    const dayMap = {};
    calendarDays.forEach((d) => {
      dayMap[d.attendanceDate] = d;
    });

    const cells = [];
    for (let i = 0; i < startDay; i += 1) cells.push(null);
    for (let day = 1; day <= totalDays; day += 1) {
      const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push(dayMap[dateString] || { attendanceDate: dateString, status: 'NA', remarks: '' });
    }
    return cells;
  }, [calendarFilters.month, calendarDays]);

  const fetchEmployeeOptions = async () => {
    try {
      setEmployeeLoading(true);
      setEmployeeError('');
      const response = await api.get('/api/v1/employees/filter_options');
      if (response?.data?.success) {
        const options = response.data.employees || [];
        setEmployeeOptions(options);
        setBulkRows(
          options.map((emp) => ({
            employeeId: emp.employeeId,
            employeeName: emp.name,
            status: 'PRESENT',
            checkIn: '',
            checkOut: '',
            remarks: '',
            error: '',
          }))
        );
      } else {
        setEmployeeError(response?.data?.message || 'Unable to load employees.');
      }
    } catch (error) {
      setEmployeeError(error?.response?.data?.message || error?.message || 'Unable to load employees.');
    } finally {
      setEmployeeLoading(false);
    }
  };

  const fetchAttendanceList = async (page = 1, perPage = listPageInfo.per_page || 20) => {
    try {
      setListLoading(true);
      setListError('');
      const response = await api.post('/api/v1/attendances/list', {
        query: {
          from: listFilters.from,
          to: listFilters.to,
          employeeId: listFilters.employeeId || '',
          status: listFilters.status || '',
        },
        page,
        per_page: perPage,
      });

      if (response?.data?.success) {
        const rows = response.data.attendances || [];
        const pageInfo = response.data.pageInfo || INITIAL_PAGE_INFO;
        setListRows(rows);
        setListPageInfo({ ...INITIAL_PAGE_INFO, ...pageInfo });

        const draft = {};
        rows.forEach((row) => {
          draft[row.id] = {
            status: row.status || 'PRESENT',
            remarks: row.remarks || '',
          };
        });
        setRowDraft(draft);
      } else {
        setListError(response?.data?.message || 'Unable to fetch attendance list.');
      }
    } catch (error) {
      setListError(error?.response?.data?.message || error?.message || 'Unable to fetch attendance list.');
    } finally {
      setListLoading(false);
    }
  };

  const fetchMonthSummary = async () => {
    try {
      setSummaryLoading(true);
      setSummaryError('');
      const response = await api.post('/api/v1/attendances/month_summary', {
        query: {
          month: summaryFilters.month,
          employeeId: summaryFilters.employeeId || '',
        },
      });

      if (response?.data?.success) {
        setSummaryRows(response.data.rows || []);
      } else {
        setSummaryError(response?.data?.message || 'Unable to fetch month summary.');
      }
    } catch (error) {
      setSummaryError(error?.response?.data?.message || error?.message || 'Unable to fetch month summary.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchEmployeeCalendar = async () => {
    if (!calendarFilters.employeeId) {
      setCalendarError('Please select employee.');
      setCalendarDays([]);
      return;
    }

    try {
      setCalendarLoading(true);
      setCalendarError('');
      const response = await api.post('/api/v1/attendances/employee_calendar', {
        query: {
          employeeId: Number(calendarFilters.employeeId),
          month: calendarFilters.month,
        },
      });

      if (response?.data?.success) {
        setCalendarDays(response.data.days || []);
      } else {
        setCalendarError(response?.data?.message || 'Unable to fetch employee calendar.');
      }
    } catch (error) {
      setCalendarError(error?.response?.data?.message || error?.message || 'Unable to fetch employee calendar.');
    } finally {
      setCalendarLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeeOptions();
  }, []);

  useEffect(() => {
    if (activeTab === 1) fetchAttendanceList(1, listPageInfo.per_page || 20);
    if (activeTab === 2) fetchMonthSummary();
    if (activeTab === 3) fetchEmployeeCalendar();
  }, [activeTab]);

  const onChangeBulkRow = (employeeId, key, value) => {
    setBulkRows((prev) =>
      prev.map((row) => (row.employeeId === employeeId ? { ...row, [key]: value, error: '' } : row))
    );
  };

  const onClickSaveBulk = async () => {
    let hasError = false;
    const validatedRows = bulkRows.map((row) => {
      let error = '';
      if (row.checkOut && !row.checkIn) error = 'Check-in required when check-out is set.';
      if (row.checkIn && row.checkOut && row.checkOut < row.checkIn) error = 'Check-out cannot be before check-in.';
      if (error) hasError = true;
      return { ...row, error };
    });

    if (hasError) {
      setBulkRows(validatedRows);
      toast.error('Please fix row errors before saving.');
      return;
    }

    const records = validatedRows.map((row) => {
      const checkInTime = toOffsetISOString(markDate, row.checkIn);
      const checkOutTime = toOffsetISOString(markDate, row.checkOut);
      return {
        employeeId: row.employeeId,
        status: row.status,
        remarks: row.remarks || '',
        ...(checkInTime ? { checkInTime } : {}),
        ...(checkOutTime ? { checkOutTime } : {}),
      };
    });

    try {
      setBulkSaving(true);
      const response = await api.post('/api/v1/attendances/mark_bulk', {
        data: {
          attendanceDate: markDate,
          records,
        },
      });

      if (response?.data?.success) {
        toast.success(response?.data?.message || 'Attendance marked successfully.');
      } else {
        toast.error(response?.data?.message || 'Failed to save attendance.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save attendance.');
    } finally {
      setBulkSaving(false);
    }
  };

  const onClickSaveRow = async (row) => {
    const draft = rowDraft[row.id];
    if (!draft) return;

    try {
      setRowSavingId(row.id);
      const response = await api.post('/api/v1/attendances/mark_single', {
        data: {
          attendanceDate: row.attendanceDate,
          employeeId: row.employeeId,
          status: draft.status,
          remarks: draft.remarks || '',
        },
      });

      if (response?.data?.success) {
        toast.success(response?.data?.message || 'Attendance saved successfully.');
        fetchAttendanceList(listPageInfo.current_page || 1, listPageInfo.per_page || 20);
      } else {
        toast.error(response?.data?.message || 'Failed to save row.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save row.');
    } finally {
      setRowSavingId(null);
    }
  };

  return (
    <Container maxWidth={false}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Attendance Management</Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Mark Attendance" />
          <Tab label="Attendance List" />
          <Tab label="Month Summary" />
          <Tab label="Employee Calendar" />
        </Tabs>
      </Card>

      {activeTab === 0 && (
        <Card sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                label="Attendance Date"
                type="date"
                size="small"
                value={markDate}
                onChange={(e) => setMarkDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" onClick={onClickSaveBulk} disabled={bulkSaving || employeeLoading}>
                Save All
              </Button>
            </Stack>

            {employeeError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={fetchEmployeeOptions}>
                    Retry
                  </Button>
                }
              >
                {employeeError}
              </Alert>
            ) : null}

            {employeeLoading ? (
              <Skeleton variant="rectangular" height={260} />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Check-In</TableCell>
                      <TableCell>Check-Out</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkRows.map((row) => (
                      <TableRow key={row.employeeId} hover>
                        <TableCell>{row.employeeName}</TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={row.status}
                            onChange={(e) => onChangeBulkRow(row.employeeId, 'status', e.target.value)}
                            sx={{ minWidth: 150 }}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={row.checkIn}
                            onChange={(e) => onChangeBulkRow(row.employeeId, 'checkIn', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={row.checkOut}
                            onChange={(e) => onChangeBulkRow(row.employeeId, 'checkOut', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.remarks}
                            onChange={(e) => onChangeBulkRow(row.employeeId, 'remarks', e.target.value)}
                            error={Boolean(row.error)}
                            helperText={row.error || ''}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {bulkRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ height: 120 }}>
                          No employees found.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </Card>
      )}

      {activeTab === 1 && (
        <Stack spacing={2}>
          <Card sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="From"
                type="date"
                size="small"
                value={listFilters.from}
                onChange={(e) => setListFilters((prev) => ({ ...prev, from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={listFilters.to}
                onChange={(e) => setListFilters((prev) => ({ ...prev, to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Employee"
                size="small"
                value={listFilters.employeeId}
                onChange={(e) => setListFilters((prev) => ({ ...prev, employeeId: e.target.value }))}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">All</MenuItem>
                {employeeOptions.map((emp) => (
                  <MenuItem key={emp.employeeId} value={String(emp.employeeId)}>
                    {emp.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Status"
                size="small"
                value={listFilters.status}
                onChange={(e) => setListFilters((prev) => ({ ...prev, status: e.target.value }))}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={() => fetchAttendanceList(1, listPageInfo.per_page || 20)}>
                Search
              </Button>
            </Stack>
          </Card>

          <Card sx={{ p: 2 }}>
            {listError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => fetchAttendanceList(listPageInfo.current_page || 1, listPageInfo.per_page || 20)}>
                    Retry
                  </Button>
                }
              >
                {listError}
              </Alert>
            ) : listLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SNo</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Check-In</TableCell>
                        <TableCell>Check-Out</TableCell>
                        <TableCell>Remarks</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listRows.map((row, index) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{(Number(listPageInfo.current_page || 1) - 1) * Number(listPageInfo.per_page || 20) + index + 1}</TableCell>
                          <TableCell>{row.attendanceDate}</TableCell>
                          <TableCell>{row.employeeName}</TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              value={rowDraft[row.id]?.status || row.status || 'PRESENT'}
                              onChange={(e) =>
                                setRowDraft((prev) => ({
                                  ...prev,
                                  [row.id]: {
                                    ...(prev[row.id] || {}),
                                    status: e.target.value,
                                  },
                                }))
                              }
                              sx={{ minWidth: 140 }}
                            >
                              {STATUS_OPTIONS.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell>{formatDateTime(row.checkInTime)}</TableCell>
                          <TableCell>{formatDateTime(row.checkOutTime)}</TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={rowDraft[row.id]?.remarks || ''}
                              onChange={(e) =>
                                setRowDraft((prev) => ({
                                  ...prev,
                                  [row.id]: {
                                    ...(prev[row.id] || {}),
                                    remarks: e.target.value,
                                  },
                                }))
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => onClickSaveRow(row)}
                              disabled={rowSavingId === row.id}
                            >
                              Save
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!listRows.length ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ height: 120 }}>
                            No attendance records found.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>
                {listRows.length > 0 ? (
                  <Stack alignItems="center" pt={2}>
                    <Pagination
                      page={Number(listPageInfo.current_page || 1)}
                      count={Number(listPageInfo.total_pages || 1)}
                      onChange={(e, page) => fetchAttendanceList(page, listPageInfo.per_page || 20)}
                    />
                  </Stack>
                ) : null}
              </>
            )}
          </Card>
        </Stack>
      )}

      {activeTab === 2 && (
        <Stack spacing={2}>
          <Card sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                type="month"
                label="Month"
                size="small"
                value={summaryFilters.month}
                onChange={(e) => setSummaryFilters((prev) => ({ ...prev, month: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Employee"
                size="small"
                value={summaryFilters.employeeId}
                onChange={(e) => setSummaryFilters((prev) => ({ ...prev, employeeId: e.target.value }))}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">All</MenuItem>
                {employeeOptions.map((emp) => (
                  <MenuItem key={emp.employeeId} value={String(emp.employeeId)}>
                    {emp.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={fetchMonthSummary}>
                Search
              </Button>
            </Stack>
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Payable Days
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                  {totalPayableDays.toFixed(1)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card sx={{ p: 2 }}>
                {summaryLoading ? (
                  <Skeleton variant="rectangular" height={280} />
                ) : summaryError ? (
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={fetchMonthSummary}>
                        Retry
                      </Button>
                    }
                  >
                    {summaryError}
                  </Alert>
                ) : summaryRows.length ? (
                  <ReactApexChart type="bar" height={280} series={summaryChartSeries} options={summaryChartOptions} />
                ) : (
                  <Typography>No summary data found.</Typography>
                )}
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ p: 2 }}>
            {summaryLoading ? (
              <Skeleton variant="rectangular" height={240} />
            ) : summaryError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={fetchMonthSummary}>
                    Retry
                  </Button>
                }
              >
                {summaryError}
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell align="center">Present</TableCell>
                      <TableCell align="center">Absent</TableCell>
                      <TableCell align="center">Half Day</TableCell>
                      <TableCell align="center">Leave</TableCell>
                      <TableCell align="center">Holiday</TableCell>
                      <TableCell align="center">Payable Days</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryRows.map((row) => (
                      <TableRow key={row.employeeId} hover>
                        <TableCell>{row.employeeName}</TableCell>
                        <TableCell align="center">{row.present}</TableCell>
                        <TableCell align="center">{row.absent}</TableCell>
                        <TableCell align="center">{row.halfDay}</TableCell>
                        <TableCell align="center">{row.leave}</TableCell>
                        <TableCell align="center">{row.holiday}</TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 700 }}>{Number(row.payableDays || 0).toFixed(1)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!summaryRows.length ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ height: 120 }}>
                          No summary rows found.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Stack>
      )}

      {activeTab === 3 && (
        <Stack spacing={2}>
          <Card sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Employee"
                size="small"
                value={calendarFilters.employeeId}
                onChange={(e) => setCalendarFilters((prev) => ({ ...prev, employeeId: e.target.value }))}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">Select Employee</MenuItem>
                {employeeOptions.map((emp) => (
                  <MenuItem key={emp.employeeId} value={String(emp.employeeId)}>
                    {emp.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="month"
                label="Month"
                size="small"
                value={calendarFilters.month}
                onChange={(e) => setCalendarFilters((prev) => ({ ...prev, month: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" onClick={fetchEmployeeCalendar}>
                Search
              </Button>
            </Stack>
          </Card>

          <Card sx={{ p: 2 }}>
            {calendarError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={fetchEmployeeCalendar}>
                    Retry
                  </Button>
                }
              >
                {calendarError}
              </Alert>
            ) : calendarLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <Typography key={d} variant="caption" sx={{ fontWeight: 700 }}>
                      {d}
                    </Typography>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    gap: 1,
                  }}
                >
                  {calendarGrid.map((day, index) => (
                    <Box
                      key={`${day?.attendanceDate || 'blank'}-${index}`}
                      sx={{
                        minHeight: 74,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.8,
                        bgcolor: day ? 'background.paper' : 'transparent',
                      }}
                    >
                      {day ? (
                        <>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.8 }}>
                            {Number(day.attendanceDate.split('-')[2])}
                          </Typography>
                          <Chip
                            size="small"
                            label={day.status}
                            color={STATUS_COLOR[day.status] || 'default'}
                            sx={{ mb: 0.5 }}
                          />
                          {day.remarks ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {day.remarks}
                            </Typography>
                          ) : null}
                        </>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Card>
        </Stack>
      )}
    </Container>
  );
}
